import { staffAuthHeaders } from '../../lib/sbAuth';
import { useState, useRef, useCallback } from 'react';
import { useLang } from '../../config/i18n';
import { TODAY } from '../../config/constants';
import { Modal, SaveBtn } from '../shared/UI';

// ── Load pdf.js from CDN on demand ────────────────────────────────
const PDFJS_CDN    = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const WORKER_CDN   = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

async function loadPdfJs() {
  if (window._pdfjsReady) return window.pdfjsLib;

  // 1. Load main pdf.js script
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = PDFJS_CDN;
    s.onload = resolve;
    s.onerror = () => reject(new Error('pdf.js load failed'));
    document.head.appendChild(s);
  });

  // 2. Fetch worker as blob to avoid CORS, fall back to direct CDN URL
  try {
    const res       = await fetch(WORKER_CDN);
    const blob      = await res.blob();
    const workerUrl = URL.createObjectURL(blob);
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
  } catch (e) {
    console.warn('Worker blob failed, using CDN directly:', e);
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_CDN;
  }
  window._pdfjsReady = true;
  return window.pdfjsLib;
}

async function extractPdfText(file) {
  const pdfjs = await loadPdfJs();
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
  let fullText = '';
  const LINE_Y_TOLERANCE = 3; // px
  // Single Arabic char — covers basic Arabic + Presentation Forms (pre-normalization)
  const IS_SINGLE_AR = /^[؀-ۿﭐ-﷿ﹰ-﻿]$/;

  for (let i = 1; i <= pdf.numPages; i++) {
    const page    = await pdf.getPage(i);
    const content = await page.getTextContent();

    // Group items by line (same y ± tolerance)
    const lines = [];
    for (const item of content.items) {
      if (!item.str) continue;
      const x = item.transform[4];
      const y = item.transform[5];
      let line = lines.find(l => Math.abs(l.y - y) <= LINE_Y_TOLERANCE);
      if (!line) { line = { y, items: [] }; lines.push(line); }
      line.items.push({ str: item.str, x });
    }

    // Sort lines top→bottom
    lines.sort((a, b) => b.y - a.y);

    const pageText = lines.map(line => {
      // Split line into "segments": runs of single-Arabic-char items vs everything else.
      // Single-Arabic-char items are individual glyphs stored in visual order — sort them
      // by x-position descending (RTL) to get logical reading order.
      // Everything else (multi-char items, numbers, Latin) keeps its content-stream order.
      const segments = [];
      let arabicRun = [];

      const flushArabicRun = () => {
        if (arabicRun.length === 0) return;
        arabicRun.sort((a, b) => b.x - a.x);
        segments.push({ type: 'arabic', str: arabicRun.map(r => r.str).join('') });
        arabicRun = [];
      };

      for (const it of line.items) {
        if (IS_SINGLE_AR.test(it.str)) {
          arabicRun.push(it);
        } else {
          flushArabicRun();
          segments.push({ type: 'other', str: it.str });
        }
      }
      flushArabicRun();

      return segments.map(s => s.str).join(' ');
    }).join('\n');

    fullText += '\n' + pageText;
  }

  // Normalize Arabic Presentation Forms → basic Unicode Arabic
  return fullText.normalize('NFKC');
}

// ── Unit type bilingual map ────────────────────────────────────────
const UNIT_TYPES = {
  apartment: { ar: 'شقة',     en: 'Apartment' },
  studio:    { ar: 'استديو',  en: 'Studio'    },
  villa:     { ar: 'فيلا',    en: 'Villa'     },
  floor:     { ar: 'دور',     en: 'Floor'     },
  office:    { ar: 'مكتب',    en: 'Office'    },
};
function unitTypeLabel(key, lang) {
  return UNIT_TYPES[key]?.[lang] || key || '—';
}

// ── Parse Ejar contract PDF text ──────────────────────────────────
// Based on actual Ejar PDF structure (bilingual AR/EN, YYYY-MM-DD dates, ﷼ amounts)
function parseNum(str) {
  if (!str) return 0;
  return Math.round(parseFloat(str.replace(/[,،]/g, '').replace(/﷼/g, '').trim()) || 0);
}

function parseEjarText(text, filename) {
  const r = {};

  // 1. Contract number — from filename (most reliable)
  const fnMatch = filename.match(/(\d+)(?:\.pdf)?$/i);
  if (fnMatch) r.ejarNum = fnMatch[1];

  // 2. Dates — Ejar uses YYYY-MM-DD format directly
  const startM = text.match(/Tenancy Start Date\s+(\d{4}-\d{2}-\d{2})/);
  if (startM) r.contractStart = startM[1];

  const endM = text.match(/Tenancy End Date\s+(\d{4}-\d{2}-\d{2})/);
  if (endM) r.contractEnd = endM[1];

  // Fallback: grab all YYYY-MM-DD dates and take earliest/latest
  if (!r.contractStart || !r.contractEnd) {
    const allDates = [...text.matchAll(/\b(\d{4}-\d{2}-\d{2})\b/g)]
      .map(m => m[1])
      .filter(d => d >= '2020-01-01' && d <= '2035-12-31');
    if (allDates.length >= 2) {
      const sorted = [...new Set(allDates)].sort();
      if (!r.contractStart) r.contractStart = sorted[0];
      if (!r.contractEnd)   r.contractEnd   = sorted[sorted.length - 1];
    }
  }

  // 3. Financial amounts — values follow ﷼ symbol
  // Pattern: "English Label ... ﷼ NUMBER"
  const fin = (label) => {
    const re = new RegExp(label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '[^\\d﷼]{0,30}﷼?\\s*([\\d,]+(?:\\.\\d+)?)');
    const m = text.match(re);
    return m ? parseNum(m[1]) : 0;
  };

  const annualRent = fin('Annual Rent');
  if (annualRent) r.annualRent = annualRent;

  const water = fin('Water Annual Amount');
  if (water) r.waterAnnual = water;

  const elec = fin('Electricity Annual Amount');
  if (elec) r.electricityAnnual = elec;

  // Total contract value (sum of rent + water if no explicit total)
  const regPay = fin('Regular Rent Payment');
  if (regPay && r.annualRent) {
    // freq = annualRent / regPay rounded
    const freq = Math.round(r.annualRent / regPay);
    if (freq >= 1 && freq <= 12) r.freq = String(freq);
  }

  // 4. Unit data — section 9
  const unitNoM = text.match(/Unit No\.\s+([A-Za-z0-9]+)/);
  if (unitNoM) r.unitNumber = unitNoM[1].toUpperCase();

  const unitTypeM = text.match(/Unit Type\s+([^\n\r]+)/);
  if (unitTypeM) {
    const raw = unitTypeM[1].trim();
    if      (/شق|apart/i.test(raw))       r.unitType = 'apartment';
    else if (/ستوديو|studio/i.test(raw))  r.unitType = 'studio';
    else if (/فيلا|villa/i.test(raw))     r.unitType = 'villa';
    else if (/دور|floor/i.test(raw))      r.unitType = 'floor';
    else if (/مكتب|office/i.test(raw))    r.unitType = 'office';
    else                                   r.unitType = raw.slice(0, 40).trim();
  }

  // 5. Payment cycle (take semi/quarter/monthly/annual)
  const cycleM = text.match(/Rent payment cycle\s+([^\n\r]+)/);
  if (cycleM) {
    const ct = cycleM[1].trim();
    if      (/ربع|quarter/i.test(ct))        r.freq = '4';
    else if (/نصف|semi|half/i.test(ct))      r.freq = '2';
    else if (/شهر|month/i.test(ct))          r.freq = '12';
    else if (/سنو|annual|year/i.test(ct))    r.freq = '1';
  }

  // Helper: extract Arabic name from a raw captured line — stop at first colon
  function extractArabicName(raw) {
    const beforeColon = raw.match(/^([؀-ۿ\s]+?)\s*:/);
    return (beforeColon ? beforeColon[1] : raw.split(/\s+/).slice(0, 7).join(' ')).trim();
  }

  // 6. Lessor company — prefer "Brokerage Entity Name" (cleaner extraction), fallback to first "Company name/Founder"
  const brokerM = text.match(/Brokerage Entity Name\s+([^:\n\r]+?)\s*:/);
  if (brokerM) {
    // Brokerage entity name is in visual order — reverse word order
    const words = brokerM[1].trim().split(/\s+/).filter(Boolean);
    r.landlordCompany = (words.every(w => /^[؀-ۿ]+$/.test(w)) ? words.reverse() : words).join(' ');
  }
  const compMatches = [...text.matchAll(/Company name\/Founder\s+([^\n\r]+)/g)];
  if (!r.landlordCompany && compMatches.length > 0) r.landlordCompany = extractArabicName(compMatches[0][1]);
  // Tenant company — second "Company name/Founder" occurrence, no word reversal (already in logical order)
  if (compMatches.length > 1) r.tenantName = extractArabicName(compMatches[1][1]);
  // Fix Ejar PDF glyph-order artifacts for known garbled words
  const ejarWordFixes = { 'راللعقات': 'للعقارات', 'راللعقارات': 'للعقارات' };
  if (r.landlordCompany) {
    r.landlordCompany = r.landlordCompany.replace(/راللعقات|راللعقارات/g, w => ejarWordFixes[w] || w);
  }

  // 7. National address — from Property Data section (last "National Address" entry)
  const addrMatches = [...text.matchAll(/National Address\s+([^\n\r]+)/g)];
  if (addrMatches.length > 0) {
    const propAddr = addrMatches.find(m => /\d{4}/.test(m[1]));
    const rawAddr = (propAddr || addrMatches[addrMatches.length - 1])[1];
    // Stop at colon (where Arabic label begins)
    const beforeColon = rawAddr.match(/^([^:]+?)\s*:/);
    r.nationalAddress = (beforeColon ? beforeColon[1] : rawAddr).trim().replace(/\s+/g, ' ');
  }

  // 8. District name — try explicit "District" field first, fallback to national address
  const districtFieldM = text.match(/District\s+([^\n\r:]+)/);
  if (districtFieldM) {
    const arPart = districtFieldM[1].match(/([؀-ۿ][؀-ۿ\s]*)/);
    if (arPart) r.district = arPart[1].trim().replace(/\s+/g, ' ');
  }
  if (!r.district && r.nationalAddress) {
    // National address format: "حطين، 13524 ،..." — grab first Arabic cluster only
    const arPart = r.nationalAddress.match(/^([؀-ۿ][؀-ۿ\s]*)/);
    if (arPart) r.district = arPart[1].trim().replace(/[،,\s]+$/, '').replace(/\s+/g, ' ');
  }
  // Build clean unit name: "رقم الوحدة - الحي"
  if (r.unitNumber && r.district) r.unitName = `${r.unitNumber} - ${r.district}`;
  else if (r.unitNumber)          r.unitName = r.unitNumber;

  // 9. Auto-generate payment dates from contractStart + freq
  if (r.contractStart && r.freq) {
    const dates = []; let d = new Date(r.contractStart);
    const freq = Number(r.freq); const step = Math.round(12 / freq);
    for (let i = 0; i < freq; i++) {
      dates.push(d.toISOString().slice(0, 10));
      d = new Date(d); d.setMonth(d.getMonth() + step);
    }
    r.paymentDates = dates.join(',');
  }

  return r;
}

// ── Payments Ledger sub-component ────────────────────────────────
const LEDGER_FILTERS = [
  { key: 'upcoming', label: 'القادمة (90 يوم)' },
  { key: 'unpaid',   label: 'غير مدفوعة'       },
  { key: 'paid',     label: 'مدفوعة'            },
  { key: 'all',      label: 'الكل'              },
];

function PaymentsLedger({ contracts, markPayment, daysUntil }) {
  const [filter, setFilter] = useState('upcoming');

  const allRows = contracts.flatMap(c => {
    const perPay = (c.annualRent && c.freq) ? Math.round(Number(c.annualRent) / Number(c.freq)) : 0;
    return (c.paymentDates || []).map(date => ({
      contractId: c.id,
      date,
      unit: c.unitNumber || c.unitName,
      district: c.district || '',
      amount: perPay,
      log: c.paymentsLog?.[date] || {},
      ejarNum: c.ejarNum,
    }));
  }).sort((a, b) => new Date(a.date) - new Date(b.date));

  const rows = allRows.filter(r => {
    const d = daysUntil(r.date);
    if (filter === 'upcoming') return d !== null && d >= 0 && d <= 90;
    if (filter === 'unpaid')   return !r.log.paid;
    if (filter === 'paid')     return !!r.log.paid;
    return true;
  });

  const totalPaid    = allRows.filter(r => r.log.paid).reduce((s, r) => s + (r.log.amount || r.amount), 0);
  const totalPending = allRows.filter(r => !r.log.paid).reduce((s, r) => s + r.amount, 0);

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8' }}>💳 سجل المدفوعات</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: '#22c55e', background: 'rgba(34,197,94,0.08)', padding: '3px 10px', borderRadius: 7, border: '1px solid rgba(34,197,94,0.2)' }}>
            ✅ مدفوع: {totalPaid.toLocaleString()} ريال
          </span>
          <span style={{ fontSize: 11, color: '#f59e0b', background: 'rgba(245,158,11,0.08)', padding: '3px 10px', borderRadius: 7, border: '1px solid rgba(245,158,11,0.2)' }}>
            ⏳ معلق: {totalPending.toLocaleString()} ريال
          </span>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {LEDGER_FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{ padding: '5px 14px', borderRadius: 8, border: '1px solid', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: "'Tajawal',sans-serif",
              background: filter === f.key ? 'rgba(200,160,60,0.15)' : 'rgba(255,255,255,0.03)',
              color: filter === f.key ? '#c8a03c' : '#64748b',
              borderColor: filter === f.key ? 'rgba(200,160,60,0.4)' : 'rgba(255,255,255,0.07)' }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, overflow: 'hidden', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
          <thead>
            <tr style={{ background: 'rgba(0,0,0,0.3)' }}>
              {['تاريخ الدفعة', 'الوحدة', 'المبلغ', 'الحالة', 'تاريخ السداد', ''].map(h => (
                <th key={h} style={{ padding: '9px 14px', textAlign: 'right', fontSize: 10, color: '#475569', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} style={{ padding: '30px', textAlign: 'center', color: '#334155', fontSize: 13 }}>لا توجد دفعات في هذه الفئة</td></tr>
            )}
            {rows.map((r, i) => {
              const d = daysUntil(r.date);
              const isPaid = !!r.log.paid;
              const isOverdue = !isPaid && d !== null && d < 0;
              const isSoon    = !isPaid && d !== null && d >= 0 && d <= 14;
              return (
                <tr key={i} className="trow" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: 13, fontWeight: 700,
                      color: isPaid ? '#22c55e' : isOverdue ? '#ef4444' : isSoon ? '#f59e0b' : '#94a3b8' }}>
                      {r.date}
                    </div>
                    {!isPaid && d !== null && (
                      <div style={{ fontSize: 10, color: isOverdue ? '#ef4444' : isSoon ? '#f59e0b' : '#475569' }}>
                        {isOverdue ? `متأخر ${Math.abs(d)} يوم` : d === 0 ? 'اليوم' : `بعد ${d} يوم`}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#c8a03c' }}>{r.unit}</div>
                    {r.district && <div style={{ fontSize: 10, color: '#475569' }}>{r.district}</div>}
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 14, fontWeight: 800, color: isPaid ? '#22c55e' : '#e2e8f0', whiteSpace: 'nowrap' }}>
                    {r.amount > 0 ? r.amount.toLocaleString() + ' ريال' : '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 7,
                      color: isPaid ? '#22c55e' : isOverdue ? '#ef4444' : '#f59e0b',
                      background: isPaid ? 'rgba(34,197,94,0.1)' : isOverdue ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)' }}>
                      {isPaid ? '✅ مدفوعة' : isOverdue ? '🔴 متأخرة' : '⏳ معلقة'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 11, color: '#64748b' }}>
                    {r.log.paidAt || '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <button
                      onClick={() => markPayment(r.contractId, r.date, !isPaid)}
                      style={{ padding: '5px 14px', borderRadius: 8, border: '1px solid', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: "'Tajawal',sans-serif", whiteSpace: 'nowrap',
                        background: isPaid ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.1)',
                        color: isPaid ? '#ef4444' : '#22c55e',
                        borderColor: isPaid ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.3)' }}>
                      {isPaid ? '↩ إلغاء' : '✓ تأكيد الدفع'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────
export default function MasterContractsTab({ contracts, setContracts, units, setUnits, monthly, setMonthly, sbConfig }) {
  const { S, lang } = useLang();
  const T = S.contracts;
  const [editing,      setEditing]      = useState(null);
  const [form,         setForm]         = useState({});
  const [search,       setSearch]       = useState('');
  const [parsing,      setParsing]      = useState(false);
  const [parseMsg,     setParseMsg]     = useState('');
  const [rawText,      setRawText]      = useState('');
  const [renewingId,   setRenewingId]   = useState(null);
  const [renewForm,    setRenewForm]    = useState({});
  const [expandedCards, setExpandedCards] = useState({});
  // Furniture purchases modal — one modal shared across all contracts, keyed by contract id.
  const [furnitureModalId, setFurnitureModalId] = useState(null);
  const [newFurniture, setNewFurniture] = useState({ date: '', desc: '', amount: '', imageDataUrl: '' });
  const [lightboxImg, setLightboxImg] = useState(null); // fullsize preview when a thumbnail is clicked
  const toggleCard = id => setExpandedCards(p => ({ ...p, [id]: !p[id] }));
  const pdfRef   = useRef(null);
  const photoRef = useRef(null);

  const today = TODAY;

  function daysUntil(dateStr) {
    return dateStr ? Math.ceil((new Date(dateStr) - today) / (1000 * 60 * 60 * 24)) : null;
  }

  function nextPayment(c) {
    if (!c.paymentDates?.length) return null;
    const log = c.paymentsLog || {};
    // Return earliest date that hasn't been paid yet
    const unpaid = c.paymentDates.filter(d => !log[d]?.paid).sort();
    if (unpaid.length) return unpaid[0];
    // All paid — show last date (contract complete)
    return c.paymentDates[c.paymentDates.length - 1];
  }

  function contractStatus(c) {
    const d = daysUntil(c.contractEnd);
    if (d === null) return { label: 'نشط', color: '#22c55e', renew: false };
    if (d < 0)     return { label: 'منتهي',          color: '#6b7280', renew: false };
    if (d <= 30)   return { label: 'ينتهي قريباً',    color: '#ef4444', renew: true,  days: d };
    if (d <= 60)   return { label: 'يحتاج تجديد',    color: '#f97316', renew: true,  days: d };
    return               { label: 'نشط',              color: '#22c55e', renew: false };
  }

  function contractMonths(c) {
    if (!c.contractStart || !c.contractEnd) return 12;
    return Math.max(1, Math.round((new Date(c.contractEnd) - new Date(c.contractStart)) / (1000 * 60 * 60 * 24 * 30.44)));
  }

  function monthlyObligation(c) {
    return (Number(c.annualRent)        || 0) / 12
         + (Number(c.waterAnnual)       || 0) / 12
         + (Number(c.electricityAnnual) || 0) / 12
         + (Number(c.officeFee)         || 0) / contractMonths(c);
  }

  // Filter chips:
  //   active   → default: unit contracts, not archived
  //   storage  → warehouses (type = 'storage') only
  //   archived → archived contracts only (mix of units + storage)
  //   all      → everything
  const [archiveFilter, setArchiveFilter] = useState('active');
  const filtered = contracts
    .filter(c => {
      if (archiveFilter === 'active')   return c.status !== 'archived' && c.type !== 'storage';
      if (archiveFilter === 'storage')  return c.status !== 'archived' && c.type === 'storage';
      if (archiveFilter === 'archived') return c.status === 'archived';
      return true; // 'all'
    })
    .filter(c =>
      !search ||
      c.unitName?.includes(search) ||
      c.ejarNum?.toLowerCase().includes(search.toLowerCase()) ||
      c.landlord?.includes(search) ||
      c.landlordCompany?.includes(search) ||
      c.name?.includes(search)
    );
  const archivedCount = contracts.filter(c => c.status === 'archived').length;
  const storageCount  = contracts.filter(c => c.type === 'storage' && c.status !== 'archived').length;

  // Portfolio totals exclude archived contracts AND storage locations — storage warehouses
  // don't have a landlord/rent, so they aren't part of your obligation totals.
  const activePortfolio = contracts.filter(c => c.status !== 'archived' && c.type !== 'storage');
  const totalAnnual   = activePortfolio.reduce((s, c) => s + (Number(c.annualRent)        || 0), 0);
  const totalMonthly  = activePortfolio.reduce((s, c) => s + monthlyObligation(c),                0);
  const totalWater    = activePortfolio.reduce((s, c) => s + (Number(c.waterAnnual)       || 0), 0);
  const totalElec     = activePortfolio.reduce((s, c) => s + (Number(c.electricityAnnual) || 0), 0);
  const totalDeposits = activePortfolio.reduce((s, c) => s + (Number(c.depositAmount)     || 0), 0);

  // Storage locations are a special kind of contract with `type: 'storage'`. They reuse
  // the furniture-purchases modal + photos + Balance Sheet asset flow, but don't have a
  // landlord, rent, Ejar, dates, or expiry — so they're excluded from lease calcs and
  // rendered with a simpler card. Editable via a dedicated storage form.
  const [storageEditingId, setStorageEditingId] = useState(null); // 'new' or an id
  const [storageForm, setStorageForm] = useState({});
  function openAddStorage() {
    setStorageForm({ name: '', address: '', notes: '' });
    setStorageEditingId('new');
  }
  function openEditStorage(c) {
    setStorageForm({ name: c.name || c.unitName || '', address: c.address || '', notes: c.notes || '' });
    setStorageEditingId(c.id);
  }
  function saveStorage() {
    if (!storageForm.name) return;
    const rec = {
      type:      'storage',
      name:      storageForm.name,
      unitName:  storageForm.name,   // reuse unitName so display components still work
      unitNumber: storageForm.name,  // ditto
      district:  'مستودع',
      address:   storageForm.address || '',
      notes:     storageForm.notes || '',
      status:    'active',
    };
    if (storageEditingId === 'new') {
      setContracts(p => [...p, { ...rec, id: `s${Date.now()}`, furniturePurchases: [] }]);
    } else {
      setContracts(p => p.map(c => c.id === storageEditingId ? { ...c, ...rec } : c));
    }
    setStorageEditingId(null);
    setStorageForm({});
  }

  function openAdd() {
    setForm({ unitName: '', district: '', city: 'الرياض', ejarNum: '', landlord: '', landlordCompany: '',
      tenantName: 'المساكن الخفية', nationalAddress: '', unitType: '', unitNumber: '',
      annualRent: '', waterAnnual: '', electricityAnnual: '', totalContractValue: '',
      depositAmount: '', depositPaidTo: '', depositReturned: false,
      furnitureValue: '', furnitureNotes: '',
      officeFee: '', officeName: '',
      freq: '2', contractStart: '', contractEnd: '', paymentDates: '', notes: '',
      imageUrl: '', mapsUrl: '' });
    setEditing('new');
    setParseMsg('');
  }

  function openEdit(c) {
    setForm({ ...c, paymentDates: (c.paymentDates || []).join(',') });
    setEditing(c.id);
    setParseMsg('');
  }

  async function handlePdfUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    setParseMsg('');
    try {
      const text   = await extractPdfText(file);
      setRawText(text.slice(0, 3000)); // debug: keep visible to verify Arabic encoding
      const parsed = parseEjarText(text, file.name);
      setForm(f => ({ ...f, ...parsed }));
      const extracted = Object.keys(parsed).filter(k => k !== 'ejarNum' && parsed[k]).length;
      if (extracted > 0) {
        setParseMsg(`✅ تم استخراج ${extracted} حقول تلقائياً — راجع وتأكد`);
        // keep rawText visible so Arabic encoding can be verified
      } else {
        setParseMsg('⚠️ تم قراءة الملف لكن لم تُستخرج بيانات — أرسل النص أدناه لإبراهيم لإصلاح النمط');
      }
    } catch (err) {
      console.error('PDF parse error:', err);
      setParseMsg(`❌ خطأ: ${err?.message || String(err)}`);
    }
    setParsing(false);
    e.target.value = '';
  }

  function save() {
    let payDates = (form.paymentDates || '').split(',').map(d => d.trim()).filter(Boolean);
    // Auto-generate if empty and we have start + freq
    if (payDates.length === 0 && form.contractStart && form.freq) {
      const freq = Number(form.freq) || 2; const step = Math.round(12 / freq);
      let d = new Date(form.contractStart);
      for (let i = 0; i < freq; i++) {
        payDates.push(d.toISOString().slice(0, 10));
        d = new Date(d); d.setMonth(d.getMonth() + step);
      }
    }
    const updated = {
      ...form,
      // Rebuild clean unit name every save
      unitName: [form.unitNumber, form.district].filter(Boolean).join(' - ') || form.unitName || '',
      // City defaults to Riyadh — keeps old contracts populated and acts as default for new ones
      city: form.city || 'الرياض',
      paymentDates: payDates,
      freq:               Number(form.freq)               || 2,
      annualRent:         Number(form.annualRent)         || 0,
      waterAnnual:        Number(form.waterAnnual)        || 0,
      electricityAnnual:  Number(form.electricityAnnual)  || 0,
      totalContractValue: Number(form.totalContractValue) || 0,
      depositAmount:      Number(form.depositAmount)      || 0,
      depositPaidTo:      form.depositPaidTo              || '',
      depositReturned:    !!form.depositReturned,
      furnitureValue:     Number(form.furnitureValue)     || 0,
      furnitureNotes:     form.furnitureNotes             || '',
      officeFee:          Number(form.officeFee)          || 0,
      officeName:         form.officeName                 || '',
    };
    if (editing === 'new') {
      setContracts(p => [...p, { ...updated, id: `c${Date.now()}`, status: 'active' }]);
    } else {
      // Auto-snapshot safety net: if the lease-affecting fields changed, freeze the OLD
      // annual cost onto every monthly record between the old contract's start and end
      // that doesn't already have a snapshot. Guarantees the historical P&L ledger stays
      // honest even for months the ops team never manually opened in Monthly Ops.
      const oldContract = contracts.find(c => c.id === editing);
      if (oldContract && setMonthly) {
        const oldAnnualCost = (Number(oldContract.annualRent)||0) + (Number(oldContract.officeFee)||0) + (Number(oldContract.waterAnnual)||0);
        const newAnnualCost = (Number(updated.annualRent)||0)      + (Number(updated.officeFee)||0)      + (Number(updated.waterAnnual)||0);
        const leaseChanged = oldAnnualCost !== newAnnualCost;
        if (leaseChanged && oldContract.contractStart && oldContract.contractEnd) {
          const months = [];
          const start = new Date(oldContract.contractStart);
          const end   = new Date(oldContract.contractEnd);
          const cur   = new Date(start.getFullYear(), start.getMonth(), 1);
          const last  = new Date(end.getFullYear(),   end.getMonth(),   1);
          while (cur <= last) {
            months.push(`${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,'0')}`);
            cur.setMonth(cur.getMonth()+1);
          }
          setMonthly(p => {
            const next = { ...p };
            months.forEach(mKey => {
              const key = `${oldContract.id}-${mKey}`;
              const existing = next[key] || {};
              if (existing.leaseCostAtRecord == null) {
                next[key] = {
                  ...existing,
                  leaseCostAtRecord: oldAnnualCost,
                  ejarAtRecord:      oldContract.ejarNum || '',
                };
              }
            });
            return next;
          });
        }
      }
      setContracts(p => p.map(c => c.id === editing ? { ...c, ...updated } : c));
    }

    setEditing(null);
  }

  // ── Archive / unarchive ─────────────────────────────────────────
  // Soft delete: keeps the contract + all its historical monthly records intact so P&L
  // history stays legitimate. Archived contracts drop out of "active portfolio" views
  // (Dashboard availability, current-month Balance Sheet totals) but still appear in
  // historical iterations across Finance.
  // ── Furniture purchases (itemised assets per unit) ──────────────
  // Each contract keeps a running list `furniturePurchases: [{id, date, desc, amount}]`.
  // The Balance Sheet sums these across active contracts as the Furniture Fixed Asset.
  // Total per contract = sum(furniturePurchases) + legacy furnitureValue (if any).
  function contractFurnitureTotal(c) {
    const items = Array.isArray(c?.furniturePurchases) ? c.furniturePurchases : [];
    const itemSum = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    return itemSum + (Number(c?.furnitureValue) || 0);
  }
  function openFurniture(id) {
    setNewFurniture({ date: TODAY.toISOString().slice(0,10), desc: '', amount: '', imageDataUrl: '' });
    setFurnitureModalId(id);
  }
  // Resize + compress the uploaded photo before storing. Phone photos are typically 3-5MB;
  // downscaling to 800px wide JPEG @ 0.7 quality brings them under ~120KB — safe to embed
  // in the contract record without bloating Supabase rows.
  async function handleFurniturePhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const MAX_W = 800;
        const scale = Math.min(1, MAX_W / img.width);
        const canvas = document.createElement('canvas');
        canvas.width  = Math.round(img.width  * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        setNewFurniture(f => ({ ...f, imageDataUrl: dataUrl }));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = ''; // allow re-selecting the same file
  }
  function addFurniture() {
    if (!newFurniture.desc || !newFurniture.amount) return;
    setContracts(p => p.map(c => c.id === furnitureModalId
      ? { ...c, furniturePurchases: [
          ...(Array.isArray(c.furniturePurchases) ? c.furniturePurchases : []),
          {
            id: Date.now(),
            date: newFurniture.date || TODAY.toISOString().slice(0,10),
            desc: newFurniture.desc,
            amount: Number(newFurniture.amount)||0,
            imageDataUrl: newFurniture.imageDataUrl || '',
          }
        ] }
      : c));
    setNewFurniture({ date: TODAY.toISOString().slice(0,10), desc: '', amount: '', imageDataUrl: '' });
  }
  function deleteFurniture(itemId) {
    setContracts(p => p.map(c => c.id === furnitureModalId
      ? { ...c, furniturePurchases: (c.furniturePurchases||[]).filter(i => i.id !== itemId) }
      : c));
  }

  function archiveContract(id) {
    if (!window.confirm('أرشفة هذا العقد؟\n\nسيبقى في السجل التاريخي والأشهر السابقة تحتفظ ببياناتها الكاملة، لكن لن يظهر ضمن الوحدات النشطة.')) return;
    setContracts(p => p.map(c => c.id === id ? { ...c, status: 'archived', archivedAt: new Date().toISOString().slice(0, 10) } : c));
  }
  function unarchiveContract(id) {
    setContracts(p => p.map(c => c.id === id ? { ...c, status: 'active', archivedAt: null } : c));
  }
  // Hard delete kept for admin cleanup only (e.g. test data). Not shown on cards by default.
  function deleteContract(id) {
    if (!window.confirm(T.deleteConfirm)) return;
    setContracts(p => p.filter(c => c.id !== id));
  }

  function openRenew(c) {
    setRenewForm({
      annualRent: c.annualRent || '',
      status: c.renewalPlan?.status || 'negotiating',
      notes: c.renewalPlan?.notes || '',
    });
    setRenewingId(c.id);
  }

  function saveRenewal() {
    setContracts(p => p.map(c => c.id === renewingId ? {
      ...c,
      renewalPlan: {
        annualRent: Number(renewForm.annualRent) || 0,
        status: renewForm.status,
        notes: renewForm.notes,
        updatedAt: TODAY.toISOString().slice(0, 10),
      },
    } : c));
    setRenewingId(null);
  }

  const handlePhotoUpload = useCallback(async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    // Convert HEIC/HEIF → JPEG
    const isHeic = file.type === 'image/heic' || file.type === 'image/heif'
      || file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
    if (isHeic) {
      try {
        if (!window._heic2any) {
          await new Promise((res, rej) => {
            const s = document.createElement('script');
            s.src = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
            s.onload = res; s.onerror = rej;
            document.head.appendChild(s);
          });
        }
        const blob = await window.heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
        file = new File([blob], file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg'), { type: 'image/jpeg' });
      } catch (err) { console.error('HEIC conversion failed:', err); }
    }
    // Try Supabase storage first
    if (sbConfig) {
      try {
        const filename = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, '-')}`;
        const res = await fetch(`${sbConfig.url}/storage/v1/object/unit-photos/${filename}`, {
          method: 'POST',
          headers: await staffAuthHeaders(sbConfig, { 'Content-Type': file.type, 'x-upsert': 'true' }),
          body: file,
        });
        if (res.ok) {
          const publicUrl = `${sbConfig.url}/storage/v1/object/public/unit-photos/${filename}`;
          setForm(f => ({ ...f, imageUrl: publicUrl }));
          e.target.value = '';
          return;
        } else { console.error('Storage upload failed:', await res.text()); }
      } catch (err) { console.error('Storage upload error:', err); }
    }
    // Fallback: base64
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, imageUrl: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [sbConfig]);

  function markPayment(contractId, date, paid) {
    const contract = contracts.find(c => c.id === contractId);
    if (!contract) return;
    const amount = (contract.annualRent && contract.freq)
      ? Math.round(Number(contract.annualRent) / Number(contract.freq))
      : 0;
    const wasPaid    = contract.paymentsLog?.[date]?.paid;
    const paidAmount = contract.paymentsLog?.[date]?.amount || amount;

    // 1. Update paymentsLog inside the contract
    setContracts(p => p.map(c => c.id === contractId ? {
      ...c,
      paymentsLog: {
        ...c.paymentsLog,
        [date]: paid
          ? { paid: true, paidAt: TODAY.toISOString().slice(0, 10), amount }
          : { paid: false },
      },
    } : c));

    // 2. Record actual cash OUTFLOW in monthly (lease expense, NOT income)
    //    monthly[contractId-YYYY-MM].leasePaidAmount tracks cash paid to landlord that month
    if (setMonthly) {
      const monthKey = date.slice(0, 7);
      const flatKey  = `${contractId}-${monthKey}`;
      setMonthly(prev => {
        const existing   = prev[flatKey] || {};
        const currentOut = Number(existing.leasePaidAmount) || 0;
        const delta      = paid ? amount : (wasPaid ? -paidAmount : 0);
        return {
          ...prev,
          [flatKey]: {
            ...existing,
            leasePaidAmount: Math.max(0, currentOut + delta),
          },
        };
      });
    }
  }

  const IS = (x = {}) => ({
    width: '100%', background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.09)', borderRadius: 8,
    padding: '8px 12px', color: '#e2e8f0', fontSize: 13, outline: 'none',
    fontFamily: "'Tajawal',sans-serif", boxSizing: 'border-box', ...x
  });

  // Grouped form fields for modal
  const formSections = [
    { title: 'بيانات العقد', cols: 2, fields: [
      ['رقم العقد (Ejar)', 'ejarNum'],
      ['بداية العقد (YYYY-MM-DD)',  'contractStart', 'datetext'],
      ['نهاية العقد (YYYY-MM-DD)', 'contractEnd',   'datetext'],
    ]},
    { title: 'بيانات المؤجر', cols: 2, fields: [
      ['اسم الشركة / المؤسسة', 'landlordCompany'],
      ['اسم المالك',            'landlord'],
    ]},
    { title: 'بيانات المستأجر', cols: 1, fields: [
      ['اسم المستأجر', 'tenantName'],
    ]},
    { title: 'بيانات العقار والوحدة', cols: 2, fields: [
      ['رقم الوحدة',     'unitNumber'],
      ['الحي / المنطقة', 'district'],
      ['المدينة',        'city'],
      ['نوع الوحدة',     'unitType', 'select'],
    ]},
    { title: 'البيانات المالية', cols: 2, fields: [
      ['الإيجار السنوي (ريال)',       'annualRent',         'number'],
      ['مياه سنوي (ريال)',            'waterAnnual',        'number'],
      ['كهرباء سنوي (ريال)',          'electricityAnnual',  'number'],
      ['إجمالي قيمة العقد (ريال)',    'totalContractValue', 'number'],
    ]},
    { title: '🛋️ الأثاث والتجهيزات (تقدير حالي)', cols: 2, fields: [
      ['قيمة الأثاث (ريال)',          'furnitureValue',     'number'],
      ['ملاحظات الأثاث',              'furnitureNotes'],
    ]},
    { title: '🔒 وديعة التأمين (قابلة للاسترداد)', cols: 2, fields: [
      ['مبلغ الوديعة (ريال)',  'depositAmount', 'number'],
      ['دُفعت إلى (اسم المكتب / المالك)', 'depositPaidTo'],
    ]},
    { title: '🏢 رسوم المكتب (غير قابلة للاسترداد)', cols: 2, fields: [
      ['رسوم المكتب (ريال)',   'officeFee',   'number'],
      ['اسم المكتب / الوسيط', 'officeName'],
    ]},
  ];

  const BADGE = (color, label) => (
    <span style={{ fontSize: 11, fontWeight: 700, color,
      background: `rgba(${color === '#22c55e' ? '34,197,94' : color === '#f59e0b' ? '245,158,11' : '107,114,128'},0.12)`,
      padding: '3px 10px', borderRadius: 8 }}>{label}</span>
  );

  return (
    <div style={{ padding: '22px 20px', maxWidth: 1400, margin: '0 auto' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#c8a03c', margin: 0 }}>{T.title}</h2>
          <p style={{ fontSize: 13, color: '#475569', margin: '4px 0 0' }}>{T.subtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '8px 16px', textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: '#64748b' }}>التزام سنوي</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#ef4444' }}>{totalAnnual.toLocaleString()} ريال</div>
          </div>
          <div style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 10, padding: '8px 16px', textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: '#64748b' }}>التزام شهري</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#f97316' }}>{Math.round(totalMonthly).toLocaleString()} ريال</div>
          </div>
          <button onClick={openAdd}
            style={{ padding: '9px 18px', borderRadius: 10, background: 'linear-gradient(135deg,#c8a03c,#9a6f10)', border: 'none', color: '#0d0f1a', fontSize: 13, fontWeight: 800, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif" }}>
            + إضافة عقد
          </button>
          <button onClick={openAddStorage}
            title="إضافة مستودع لتخزين الأثاث غير المستخدم"
            style={{ padding: '9px 18px', borderRadius: 10, background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.35)', color: '#38bdf8', fontSize: 13, fontWeight: 800, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif" }}>
            🏬 إضافة مستودع
          </button>
        </div>
      </div>

      {/* ── Renewal Plans (action banner — top of page) ── */}
      {contracts.some(c => c.renewalPlan || contractStatus(c).renew) && (
        <div style={{ marginBottom: 24, background: 'rgba(249,115,22,0.05)', border: '2px solid rgba(249,115,22,0.35)', borderRadius: 16, padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 18 }}>🔔</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: '#f97316' }}>خطط التجديد — عقود تنتهي قريباً</span>
            <span style={{ marginRight: 'auto', background: 'rgba(249,115,22,0.15)', color: '#f97316', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>
              يتطلب إجراء
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
            {contracts.filter(c => c.renewalPlan || contractStatus(c).renew).map(c => {
              const st2       = contractStatus(c);
              const rp        = c.renewalPlan;
              const current   = Number(c.annualRent) || 0;
              const planned   = rp ? Number(rp.annualRent) : current;
              const STATUS_MAP = {
                negotiating:  { label: '🤝 تحت التفاوض',  color: '#f59e0b' },
                confirmed:    { label: '✅ مؤكد',          color: '#22c55e' },
                not_renewing: { label: '❌ لن يتجدد',      color: '#ef4444' },
              };
              const rpStatus = rp ? (STATUS_MAP[rp.status] || STATUS_MAP.negotiating) : null;
              return (
                <div key={c.id} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${rp ? 'rgba(34,197,94,0.2)' : 'rgba(249,115,22,0.2)'}`, borderRadius: 14, padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#c8a03c' }}>{c.unitNumber || c.unitName}</div>
                      {c.district && <div style={{ fontSize: 11, color: '#64748b' }}>{c.district}{c.city ? ` · ${c.city}` : ' · الرياض'}</div>}
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 10, color: '#64748b' }}>ينتهي</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: st2.color }}>{c.contractEnd}</div>
                      {st2.days !== undefined && <div style={{ fontSize: 10, color: st2.color }}>بعد {st2.days} يوم</div>}
                    </div>
                  </div>
                  {(() => {
                    const freq = Number(c.freq) || 2;
                    const currentPerPay = Math.round(current / freq);
                    const plannedPerPay = Math.round(planned / freq);
                    const payDiff = plannedPerPay - currentPerPay;
                    return (
                      <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                          <span style={{ color: '#64748b' }}>الإيجار الحالي</span>
                          <div style={{ textAlign: 'left' }}>
                            <span style={{ color: '#ef4444', fontWeight: 700 }}>{currentPerPay.toLocaleString()} ريال/دفعة</span>
                            <div style={{ fontSize: 10, color: '#475569' }}>{current.toLocaleString()} سنوياً · {freq} دفعات</div>
                          </div>
                        </div>
                        {rp && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                            <span style={{ color: '#64748b' }}>الإيجار المخطط</span>
                            <div style={{ textAlign: 'left' }}>
                              <span style={{ color: payDiff > 0 ? '#ef4444' : payDiff < 0 ? '#22c55e' : '#94a3b8', fontWeight: 700 }}>
                                {plannedPerPay.toLocaleString()} ريال/دفعة
                                {payDiff !== 0 && <span style={{ fontSize: 10, marginRight: 4 }}>{payDiff > 0 ? `↑+${payDiff.toLocaleString()}` : `↓${payDiff.toLocaleString()}`}</span>}
                              </span>
                              <div style={{ fontSize: 10, color: '#475569' }}>{planned.toLocaleString()} سنوياً</div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {rpStatus && (
                    <div style={{ fontSize: 11, fontWeight: 700, color: rpStatus.color, marginBottom: 6 }}>{rpStatus.label}</div>
                  )}
                  {rp?.notes && (
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, fontStyle: 'italic' }}>{rp.notes}</div>
                  )}
                  <button onClick={() => openRenew(c)}
                    style={{ width: '100%', padding: '7px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif",
                      background: rp ? 'rgba(200,160,60,0.1)' : 'rgba(249,115,22,0.1)',
                      border: `1px solid ${rp ? 'rgba(200,160,60,0.25)' : 'rgba(249,115,22,0.3)'}`,
                      color: rp ? '#c8a03c' : '#f97316' }}>
                    {rp ? '✏️ تعديل الخطة' : '📋 خطط للتجديد'}
                  </button>
                </div>
              );
            })}
          </div>
          {contracts.some(c => c.renewalPlan?.status !== 'not_renewing' && contractStatus(c).renew) && (
            <div style={{ marginTop: 14, background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>إجمالي الالتزام المتوقع عند التجديد</div>
                <div style={{ fontSize: 11, color: '#475569' }}>بناءً على العقود التي تنتهي قريباً (استثناء "لن يتجدد")</div>
              </div>
              <div style={{ textAlign: 'left' }}>
                {(() => {
                  const renewingContracts = contracts.filter(c => contractStatus(c).renew && c.renewalPlan?.status !== 'not_renewing');
                  const totalFirstPayment = renewingContracts.reduce((s, c) => {
                    const rent = c.renewalPlan?.annualRent ? Number(c.renewalPlan.annualRent) : Number(c.annualRent) || 0;
                    const freq = Number(c.freq) || 2;
                    return s + Math.round(rent / freq);
                  }, 0);
                  const totalAnnual = renewingContracts.reduce((s, c) =>
                    s + (c.renewalPlan?.annualRent ? Number(c.renewalPlan.annualRent) : Number(c.annualRent) || 0), 0);
                  return (
                    <>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#f97316' }}>{totalFirstPayment.toLocaleString()} ريال</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>الدفعة الأولى (بنفس دورة كل عقد)</div>
                      <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{totalAnnual.toLocaleString()} ريال سنوياً</div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Payments Ledger ── */}
      {contracts.some(c => c.paymentDates?.length > 0) && (
        <PaymentsLedger contracts={contracts} markPayment={markPayment} daysUntil={daysUntil} />
      )}

      {/* ── Search + archive filter chips ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={T.searchPh}
          style={{ ...IS({ direction: 'rtl', textAlign: 'right', maxWidth: 350 }) }} />
        <div style={{ display: 'flex', gap: 5 }}>
          {[
            { key: 'active',   label: '🟢 نشطة' },
            { key: 'storage',  label: `🏬 المستودعات${storageCount>0?` (${storageCount})`:''}` },
            { key: 'archived', label: `📦 الأرشيف${archivedCount>0?` (${archivedCount})`:''}` },
            { key: 'all',      label: '👁️ الكل' },
          ].map(f => (
            <button key={f.key} onClick={() => setArchiveFilter(f.key)}
              style={{ padding: '6px 12px', borderRadius: 8, cursor: 'pointer', border: '1px solid',
                fontSize: 12, fontWeight: 700, fontFamily: "'Tajawal',sans-serif",
                background: archiveFilter === f.key ? 'rgba(200,160,60,0.18)' : 'rgba(255,255,255,0.03)',
                color: archiveFilter === f.key ? '#c8a03c' : '#64748b',
                borderColor: archiveFilter === f.key ? 'rgba(200,160,60,0.4)' : 'transparent' }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Expiry alerts ── */}
      {contracts.filter(c => { const d = daysUntil(c.contractEnd); return d !== null && d >= 0 && d <= 60; }).length > 0 && (
        <div style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, padding: '10px 16px', marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fbbf24' }}>{T.expiring60}</span>
          {contracts.filter(c => { const d = daysUntil(c.contractEnd); return d !== null && d >= 0 && d <= 60; }).map(c => (
            <span key={c.id} style={{ fontSize: 12, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '3px 10px', borderRadius: 6 }}>
              {c.unitName} · {daysUntil(c.contractEnd)} يوم
            </span>
          ))}
        </div>
      )}

      {/* ── Contracts Cards ── */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#334155', fontSize: 14, padding: '40px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, marginBottom: 24 }}>
          لا توجد عقود — اضغط "+ إضافة عقد" وارفع PDF من إيجار
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16, marginBottom: 24 }}>
          {filtered.map(c => {
            const st      = contractStatus(c);
            const np      = nextPayment(c);
            const npD     = daysUntil(np);
            const npPaid  = np ? !!(c.paymentsLog?.[np]?.paid) : false;
            const perPay  = c.annualRent && c.freq ? Math.round(c.annualRent / c.freq) : 0;
            const monthly = monthlyObligation(c);
            const rent    = (Number(c.annualRent) || 0) / 12;
            const water   = (Number(c.waterAnnual) || 0) / 12;
            const elec    = (Number(c.electricityAnnual) || 0) / 12;
            const office  = (Number(c.officeFee) || 0) / contractMonths(c);
            const isExpanded = !!expandedCards[c.id];
            const isArchived = c.status === 'archived';
            const isStorage  = c.type === 'storage';

            // Storage cards use a simplified layout — no rent breakdown, no expiry, no landlord.
            // Just a name, address, furniture total, and the same "🛋️ إضافة أثاث" flow.
            if (isStorage) {
              const furnTotal = contractFurnitureTotal(c);
              const furnItems = (c.furniturePurchases || []).length;
              return (
                <div key={c.id} style={{ background: 'rgba(56,189,248,0.04)', border: `1px solid ${isArchived ? 'rgba(148,163,184,0.25)' : 'rgba(56,189,248,0.28)'}`, borderRadius: 16, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10, opacity: isArchived ? 0.55 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#38bdf8' }}>🏬 {c.name || c.unitName || 'مستودع'}</div>
                      {c.address && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>{c.address}</div>}
                      {c.notes && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{c.notes}</div>}
                    </div>
                    <span style={{ fontSize: 10, background: 'rgba(56,189,248,0.12)', color: '#38bdf8', padding: '3px 10px', borderRadius: 6, whiteSpace: 'nowrap' }}>مستودع</span>
                  </div>
                  <div style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.18)', borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>🛋️ إجمالي الأثاث المخزَّن</span>
                    <span style={{ fontSize: 16, fontWeight: 800, color: '#38bdf8' }}>
                      {furnTotal.toLocaleString()} ريال
                      {furnItems > 0 && <span style={{ fontSize: 10, color: '#64748b', marginRight: 6 }}>({furnItems} قطعة)</span>}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 'auto', paddingTop: 4, flexWrap: 'wrap' }}>
                    <button onClick={() => openFurniture(c.id)}
                      style={{ flex: 1, minWidth: 120, padding: '9px', borderRadius: 8, background: 'rgba(56,189,248,0.14)', border: '1px solid rgba(56,189,248,0.3)', color: '#38bdf8', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif" }}>
                      🛋️ إضافة أثاث
                    </button>
                    <button onClick={() => openEditStorage(c)}
                      style={{ padding: '9px 12px', borderRadius: 8, background: 'rgba(200,160,60,0.1)', border: '1px solid rgba(200,160,60,0.2)', color: '#c8a03c', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif" }}>
                      ✏️ تعديل
                    </button>
                    {isArchived
                      ? <button onClick={() => unarchiveContract(c.id)}
                          style={{ padding: '9px 12px', borderRadius: 8, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', color: '#22c55e', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif" }}>↩️</button>
                      : <button onClick={() => archiveContract(c.id)}
                          style={{ padding: '9px 12px', borderRadius: 8, background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.25)', color: '#94a3b8', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif" }}>📦</button>}
                  </div>
                </div>
              );
            }

            const borderColor = isArchived ? 'rgba(148,163,184,0.25)'
              : st.days <= 30 ? 'rgba(239,68,68,0.4)'
              : st.renew ? 'rgba(249,115,22,0.35)'
              : 'rgba(255,255,255,0.08)';
            return (
              <div key={c.id} style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${borderColor}`, borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column', opacity: isArchived ? 0.55 : 1 }}>

                {/* Photo */}
                {c.imageUrl ? (
                  <div style={{ position: 'relative' }}>
                    <img src={c.imageUrl} alt="" style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} />
                    <div style={{ position: 'absolute', top: 8, left: 8 }}>{BADGE(st.color, st.label)}</div>
                    {c.mapsUrl && (
                      <a href={c.mapsUrl} target="_blank" rel="noreferrer"
                        style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.65)', color: '#22c55e', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, textDecoration: 'none', backdropFilter: 'blur(4px)' }}>
                        📍 الموقع
                      </a>
                    )}
                  </div>
                ) : (
                  <div style={{ background: 'rgba(0,0,0,0.25)', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px' }}>
                    {BADGE(st.color, st.label)}
                    {c.mapsUrl && (
                      <a href={c.mapsUrl} target="_blank" rel="noreferrer"
                        style={{ color: '#22c55e', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>📍 الموقع</a>
                    )}
                  </div>
                )}

                {/* Card body */}
                <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>

                  {/* Unit name */}
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#c8a03c' }}>{c.unitNumber || c.unitName}</div>
                    {c.district && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{c.district}{c.city ? ` · ${c.city}` : ' · الرياض'}</div>}
                    {c.unitType  && <div style={{ fontSize: 10, color: '#475569' }}>{unitTypeLabel(c.unitType, lang)}</div>}
                  </div>

                  {/* Cost breakdown */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {rent > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#64748b' }}>إيجار</span>
                        <span style={{ color: '#ef4444', fontWeight: 700 }}>{Math.round(rent).toLocaleString()} ريال</span>
                      </div>
                    )}
                    {water > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#64748b' }}>💧 مياه</span>
                        <span style={{ color: '#38bdf8', fontWeight: 600 }}>{Math.round(water).toLocaleString()} ريال</span>
                      </div>
                    )}
                    {elec > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#64748b' }}>⚡ كهرباء</span>
                        <span style={{ color: '#fbbf24', fontWeight: 600 }}>{Math.round(elec).toLocaleString()} ريال</span>
                      </div>
                    )}
                    {office > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#64748b' }}>🏢 رسوم مكتب (شهري)</span>
                        <span style={{ color: '#a78bfa', fontWeight: 600 }}>{Math.round(office).toLocaleString()} ريال</span>
                      </div>
                    )}
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 4, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700 }}>نقطة التعادل</span>
                      <span style={{ fontSize: 18, color: '#f97316', fontWeight: 800 }}>{Math.round(monthly).toLocaleString()} ريال</span>
                    </div>
                  </div>

                  {/* Next payment pill */}
                  {np && (
                    <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: '7px 10px', fontSize: 11 }}>
                      <span style={{ color: '#64748b' }}>الدفعة القادمة: </span>
                      <span style={{ color: npPaid ? '#22c55e' : npD !== null && npD <= 7 ? '#ef4444' : npD !== null && npD <= 30 ? '#f59e0b' : '#94a3b8', fontWeight: 700 }}>{np}</span>
                      {!npPaid && npD !== null && (
                        <span style={{ color: npD < 0 ? '#ef4444' : npD <= 7 ? '#ef4444' : '#64748b', marginRight: 6 }}>
                          · {npD < 0 ? `متأخر ${Math.abs(npD)} يوم` : npD === 0 ? 'اليوم' : `${npD} يوم`}
                        </span>
                      )}
                      {npPaid && <span style={{ color: '#22c55e', marginRight: 6 }}>✅</span>}
                    </div>
                  )}

                  {/* Expand/collapse toggle */}
                  <button onClick={() => toggleCard(c.id)}
                    style={{ width: '100%', padding: '6px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', color: '#475569', fontSize: 11, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif" }}>
                    {isExpanded ? '▲ إخفاء التفاصيل' : '▼ عرض التفاصيل'}
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
                      {c.ejarNum && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span style={{ color: '#64748b' }}>رقم إيجار</span>
                          <span style={{ fontFamily: 'monospace', color: '#38bdf8' }}>{c.ejarNum}</span>
                        </div>
                      )}
                      {(c.landlordCompany || c.landlord) && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span style={{ color: '#64748b' }}>المؤجر</span>
                          <span style={{ color: '#94a3b8', textAlign: 'left', maxWidth: '60%' }}>{c.landlordCompany || c.landlord}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                        <span style={{ color: '#64748b' }}>الدورة</span>
                        <span style={{ color: '#a78bfa', fontWeight: 700 }}>{c.freq} دفعات · {perPay > 0 ? perPay.toLocaleString() + ' ر/دفعة' : '—'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                        <span style={{ color: '#64748b' }}>البداية</span>
                        <span style={{ color: '#94a3b8' }}>{c.contractStart || '—'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                        <span style={{ color: '#64748b' }}>النهاية</span>
                        <span style={{ color: daysUntil(c.contractEnd) !== null && daysUntil(c.contractEnd) <= 60 ? '#f59e0b' : '#94a3b8' }}>{c.contractEnd || '—'}</span>
                      </div>
                      {c.depositAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span style={{ color: '#64748b' }}>الوديعة</span>
                          <span style={{ color: c.depositReturned ? '#22c55e' : '#f59e0b', fontWeight: 700 }}>
                            {c.depositReturned ? '✅' : '🔒'} {Number(c.depositAmount).toLocaleString()} ريال
                          </span>
                        </div>
                      )}
                      {c.annualRent > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span style={{ color: '#64748b' }}>الإيجار السنوي</span>
                          <span style={{ color: '#ef4444', fontWeight: 700 }}>{Number(c.annualRent).toLocaleString()} ريال</span>
                        </div>
                      )}
                      {/* Payment dates */}
                      {c.paymentDates?.length > 0 && (
                        <div style={{ marginTop: 4 }}>
                          <div style={{ fontSize: 10, color: '#475569', marginBottom: 5 }}>جدول الدفعات:</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {c.paymentDates.map(d => {
                              const paid = !!(c.paymentsLog?.[d]?.paid);
                              const dd   = daysUntil(d);
                              return (
                                <div key={d} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0' }}>
                                  <span style={{ color: paid ? '#22c55e' : dd !== null && dd < 0 ? '#ef4444' : '#64748b' }}>{d}</span>
                                  <span style={{ color: paid ? '#22c55e' : dd !== null && dd < 0 ? '#ef4444' : '#475569', fontSize: 10 }}>
                                    {paid ? '✅ مدفوعة' : dd !== null && dd < 0 ? '🔴 متأخرة' : '⏳ معلقة'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Furniture assets summary — shows total + purchases count if any */}
                  {(contractFurnitureTotal(c) > 0 || (c.furniturePurchases||[]).length > 0) && (
                    <div style={{ background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: 8, padding: '6px 12px', marginTop: 6, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>🛋️ الأثاث</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#38bdf8' }}>
                        {contractFurnitureTotal(c).toLocaleString()} ريال
                        {(c.furniturePurchases||[]).length > 0 && <span style={{ fontSize: 10, color: '#64748b', marginRight: 4 }}> ({(c.furniturePurchases||[]).length})</span>}
                      </span>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 6, marginTop: 'auto', paddingTop: 4, flexWrap: 'wrap' }}>
                    <button onClick={() => openEdit(c)}
                      style={{ flex: 1, minWidth: 90, padding: '7px', borderRadius: 8, background: 'rgba(200,160,60,0.1)', border: '1px solid rgba(200,160,60,0.2)', color: '#c8a03c', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif" }}>
                      ✏️ تعديل
                    </button>
                    <button onClick={() => openFurniture(c.id)}
                      title="إضافة أثاث أو تجهيزات جديدة (تُضاف كأصل ثابت)"
                      style={{ padding: '7px 12px', borderRadius: 8, background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif" }}>
                      🛋️ إضافة أثاث
                    </button>
                    {/* Archive = soft delete. Historical monthly records + all past P&L data stay intact. */}
                    {c.status === 'archived'
                      ? <button onClick={() => unarchiveContract(c.id)}
                          title="إعادة تفعيل العقد"
                          style={{ padding: '7px 12px', borderRadius: 8, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', color: '#22c55e', fontSize: 12, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif", fontWeight: 700 }}>
                          ↩️ إعادة تفعيل
                        </button>
                      : <button onClick={() => archiveContract(c.id)}
                          title="أرشفة العقد (يحتفظ بالسجل التاريخي)"
                          style={{ padding: '7px 12px', borderRadius: 8, background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.25)', color: '#94a3b8', fontSize: 12, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif", fontWeight: 700 }}>
                          📦 أرشفة
                        </button>
                    }
                    {st.renew && (
                      <button onClick={() => openRenew(c)}
                        style={{ flex: 1, padding: '7px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif",
                          background: c.renewalPlan ? 'rgba(34,197,94,0.1)' : 'rgba(249,115,22,0.1)',
                          border: `1px solid ${c.renewalPlan ? 'rgba(34,197,94,0.25)' : 'rgba(249,115,22,0.3)'}`,
                          color: c.renewalPlan ? '#22c55e' : '#f97316' }}>
                        {c.renewalPlan ? '✅ تجديد' : '🔄 تجديد'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}


      {/* ── Deposits Tracker ── */}
      {contracts.some(c => c.depositAmount > 0) && (
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#94a3b8', marginBottom: 12 }}>🔒 متابعة الودائع والرسوم</div>
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(0,0,0,0.3)' }}>
                  {['الوحدة', 'دُفعت إلى', 'مبلغ الوديعة', 'رسوم المكتب', 'نهاية العقد', 'الحالة'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: 'right', fontSize: 10, color: '#475569', fontWeight: 700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contracts.filter(c => c.depositAmount > 0).map(c => (
                  <tr key={c.id} className="trow" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700, color: '#c8a03c' }}>
                      {c.unitNumber || c.unitName}
                      {c.district && <div style={{ fontSize: 10, color: '#475569' }}>{c.district}{c.city ? ` · ${c.city}` : ' · الرياض'}</div>}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8' }}>{c.depositPaidTo || '—'}</td>
                    <td style={{ padding: '10px 14px', fontSize: 14, fontWeight: 800, color: c.depositReturned ? '#22c55e' : '#f59e0b' }}>
                      {Number(c.depositAmount).toLocaleString()} ريال
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>
                      {c.officeFee > 0 ? <>{Number(c.officeFee).toLocaleString()} ريال{c.officeName && <div style={{ fontSize: 10 }}>{c.officeName}</div>}</> : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{c.contractEnd || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <button onClick={() => { openEdit(c); }}
                        style={{ fontSize: 11, fontWeight: 700, padding: '3px 12px', borderRadius: 7, border: '1px solid', cursor: 'pointer', fontFamily: "'Tajawal',sans-serif",
                          background: c.depositReturned ? 'rgba(34,197,94,0.08)' : 'rgba(245,158,11,0.08)',
                          color: c.depositReturned ? '#22c55e' : '#f59e0b',
                          borderColor: c.depositReturned ? 'rgba(34,197,94,0.25)' : 'rgba(245,158,11,0.25)' }}>
                        {c.depositReturned ? '✅ مُستردة' : '⏳ قيد الانتظار'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <td colSpan={2} style={{ padding: '10px 14px', fontSize: 12, color: '#64748b', fontWeight: 700 }}>الإجمالي</td>
                  <td style={{ padding: '10px 14px', fontSize: 15, fontWeight: 800, color: '#f59e0b' }}>
                    {contracts.filter(c => c.depositAmount > 0 && !c.depositReturned).reduce((s, c) => s + (Number(c.depositAmount) || 0), 0).toLocaleString()} ريال
                    <div style={{ fontSize: 10, color: '#64748b', fontWeight: 400 }}>لم تُسترد بعد</div>
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}


      {/* ── Storage Location Modal (add / edit warehouse) ── */}
      {storageEditingId && (
        <Modal title={storageEditingId === 'new' ? '🏬 إضافة مستودع' : '🏬 تعديل بيانات المستودع'} onClose={() => { setStorageEditingId(null); setStorageForm({}); }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>اسم المستودع (مطلوب)</label>
            <input type="text" placeholder="مثال: مستودع الرياض الشمالي" value={storageForm.name || ''} onChange={e => setStorageForm(f => ({ ...f, name: e.target.value }))}
              style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 12px', color: '#e2e8f0', fontSize: 14, outline: 'none', direction: 'rtl', textAlign: 'right', fontFamily: "'Tajawal',sans-serif" }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>العنوان</label>
            <input type="text" placeholder="عنوان المستودع" value={storageForm.address || ''} onChange={e => setStorageForm(f => ({ ...f, address: e.target.value }))}
              style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 12px', color: '#e2e8f0', fontSize: 14, outline: 'none', direction: 'rtl', textAlign: 'right', fontFamily: "'Tajawal',sans-serif" }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>ملاحظات</label>
            <textarea rows={3} placeholder="أي ملاحظات (المفتاح، ساعات الوصول، إلخ)" value={storageForm.notes || ''} onChange={e => setStorageForm(f => ({ ...f, notes: e.target.value }))}
              style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 12px', color: '#e2e8f0', fontSize: 13, outline: 'none', direction: 'rtl', textAlign: 'right', fontFamily: "'Tajawal',sans-serif", resize: 'vertical' }} />
          </div>
          <button onClick={saveStorage} disabled={!storageForm.name}
            style={{ width: '100%', padding: 12, borderRadius: 10, background: storageForm.name ? 'linear-gradient(135deg,#38bdf8,#0284c7)' : 'rgba(255,255,255,0.05)', border: 'none', color: storageForm.name ? '#0d0f1a' : '#334155', fontSize: 14, fontWeight: 800, cursor: storageForm.name ? 'pointer' : 'not-allowed', fontFamily: "'Tajawal',sans-serif" }}>
            💾 حفظ المستودع
          </button>
        </Modal>
      )}

      {/* ── Furniture Purchases Modal ── */}
      {furnitureModalId && (() => {
        const fc = contracts.find(c => c.id === furnitureModalId);
        if (!fc) return null;
        const items = Array.isArray(fc.furniturePurchases) ? fc.furniturePurchases : [];
        const total = items.reduce((s,i)=>s+(Number(i.amount)||0),0) + (Number(fc.furnitureValue)||0);
        return (
          <Modal title={`🛋️ الأثاث والتجهيزات — وحدة ${fc.unitNumber || fc.unitName}`} onClose={() => setFurnitureModalId(null)}>
            {/* Summary bar */}
            <div style={{ marginBottom: 16, background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 10, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 12, color: '#64748b' }}>إجمالي قيمة الأثاث</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#38bdf8' }}>{total.toLocaleString()} ريال</div>
              </div>
              <div style={{ textAlign: 'left', fontSize: 11, color: '#475569' }}>
                {items.length} عملية شراء
                {Number(fc.furnitureValue)>0 && <div style={{ marginTop: 2 }}>+ رصيد سابق: {Number(fc.furnitureValue).toLocaleString()} ريال</div>}
              </div>
            </div>

            {/* Add new item form */}
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10, fontWeight: 700 }}>إضافة أثاث جديد</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <input type="date" value={newFurniture.date} onChange={e=>setNewFurniture(f=>({...f,date:e.target.value}))}
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '9px 12px', color: '#e2e8f0', fontSize: 13, outline: 'none', fontFamily: "'Tajawal',sans-serif" }} />
                <input type="number" placeholder="المبلغ (ريال)" value={newFurniture.amount} onChange={e=>setNewFurniture(f=>({...f,amount:e.target.value}))}
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '9px 12px', color: '#e2e8f0', fontSize: 13, outline: 'none', fontFamily: "'Tajawal',sans-serif", direction: 'ltr', textAlign: 'right' }} />
              </div>
              <input type="text" placeholder="الوصف (مثال: كنبة جديدة، ثلاجة، مكيف)" value={newFurniture.desc} onChange={e=>setNewFurniture(f=>({...f,desc:e.target.value}))}
                style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '9px 12px', color: '#e2e8f0', fontSize: 13, outline: 'none', direction: 'rtl', textAlign: 'right', fontFamily: "'Tajawal',sans-serif", marginBottom: 10 }} />
              {/* Photo upload — optional, resized to 800px JPEG for reasonable storage. Helps the ops manager see what's in inventory. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <label style={{ flex: 1, padding: '9px 12px', borderRadius: 8, background: 'rgba(56,189,248,0.08)', border: '1px dashed rgba(56,189,248,0.3)', color: '#38bdf8', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif", textAlign: 'center' }}>
                  📷 {newFurniture.imageDataUrl ? 'تغيير الصورة' : 'إضافة صورة (اختياري)'}
                  <input type="file" accept="image/*" capture="environment" onChange={handleFurniturePhoto} style={{ display: 'none' }} />
                </label>
                {newFurniture.imageDataUrl && (
                  <div style={{ position: 'relative' }}>
                    <img src={newFurniture.imageDataUrl} alt="preview" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(56,189,248,0.3)' }} />
                    <button onClick={()=>setNewFurniture(f=>({...f,imageDataUrl:''}))} title="حذف الصورة"
                      style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#ef4444', color: '#fff', border: 'none', fontSize: 10, cursor: 'pointer', lineHeight: 1 }}>✕</button>
                  </div>
                )}
              </div>
              <button onClick={addFurniture} disabled={!newFurniture.desc || !newFurniture.amount}
                style={{ width: '100%', padding: '10px', borderRadius: 10, background: newFurniture.desc && newFurniture.amount ? 'linear-gradient(135deg,#38bdf8,#0284c7)' : 'rgba(255,255,255,0.05)', border: 'none', color: newFurniture.desc && newFurniture.amount ? '#0d0f1a' : '#334155', fontSize: 13, fontWeight: 800, cursor: newFurniture.desc && newFurniture.amount ? 'pointer' : 'not-allowed', fontFamily: "'Tajawal',sans-serif" }}>
                ➕ إضافة إلى الأصول
              </button>
            </div>

            {/* Existing items list */}
            {items.length > 0 && (
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 8 }}>سجل المشتريات</div>
                {[...items].sort((a,b)=>(b.date||'').localeCompare(a.date||'')).map(item => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid rgba(255,255,255,0.04)', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                      {item.imageDataUrl
                        ? <img src={item.imageDataUrl} alt={item.desc}
                            onClick={()=>setLightboxImg(item.imageDataUrl)}
                            style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(56,189,248,0.3)', cursor: 'pointer', flexShrink: 0 }} />
                        : <div style={{ width: 44, height: 44, borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#334155', flexShrink: 0 }}>🛋️</div>}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.desc}</div>
                        <div style={{ fontSize: 10, color: '#475569', marginTop: 1 }}>{item.date}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#38bdf8' }}>{Number(item.amount).toLocaleString()} ريال</span>
                      <button onClick={()=>deleteFurniture(item.id)} title="حذف"
                        style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Modal>
        );
      })()}

      {/* ── Full-size photo lightbox ── */}
      {lightboxImg && (
        <div onClick={()=>setLightboxImg(null)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: 20 }}>
          <img src={lightboxImg} alt="furniture" style={{ maxWidth: '95%', maxHeight: '95vh', borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }} />
          <button onClick={()=>setLightboxImg(null)}
            style={{ position: 'absolute', top: 20, left: 20, width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', fontSize: 18, cursor: 'pointer', fontWeight: 800 }}>✕</button>
        </div>
      )}

      {/* ── Renewal Modal ── */}
      {renewingId && (() => {
        const rc = contracts.find(c => c.id === renewingId);
        if (!rc) return null;
        return (
          <Modal title={`خطة التجديد — ${rc.unitNumber || rc.unitName}`} onClose={() => setRenewingId(null)}>
            <div style={{ marginBottom: 16, background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>الإيجار الحالي</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#ef4444' }}>{Number(rc.annualRent).toLocaleString()} ريال/سنة</div>
              <div style={{ fontSize: 13, color: '#f59e0b', fontWeight: 700, marginTop: 2 }}>
                {Math.round(Number(rc.annualRent) / (Number(rc.freq) || 2)).toLocaleString()} ريال/دفعة × {Number(rc.freq) || 2} دفعات
              </div>
              <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>ينتهي {rc.contractEnd} · بعد {daysUntil(rc.contractEnd)} يوم</div>
            </div>
            {/* Expected rent */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>الإيجار المتوقع للتجديد (ريال/سنة)</label>
              <input type="number" value={renewForm.annualRent} onChange={e => setRenewForm(f => ({ ...f, annualRent: e.target.value }))}
                style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 8, padding: '9px 12px', color: '#e2e8f0', fontSize: 15, outline: 'none', direction: 'ltr', textAlign: 'left', fontFamily: "'Tajawal',sans-serif" }} />
              {renewForm.annualRent > 0 && (() => {
                const diff = Number(renewForm.annualRent) - (Number(rc.annualRent) || 0);
                return diff !== 0 ? (
                  <div style={{ fontSize: 11, marginTop: 5, color: diff > 0 ? '#ef4444' : '#22c55e', fontWeight: 700 }}>
                    {diff > 0 ? `↑ زيادة ${diff.toLocaleString()} ريال/سنة` : `↓ تخفيض ${Math.abs(diff).toLocaleString()} ريال/سنة`}
                    {' — '}
                    {Math.round(Math.abs(diff) / 12).toLocaleString()} ريال/شهر
                  </div>
                ) : <div style={{ fontSize: 11, marginTop: 5, color: '#22c55e' }}>✅ نفس السعر الحالي</div>;
              })()}
            </div>
            {/* Status */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>حالة التفاوض</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['negotiating','🤝 تحت التفاوض','#f59e0b'],['confirmed','✅ مؤكد','#22c55e'],['not_renewing','❌ لن يتجدد','#ef4444']].map(([v, l, col]) => (
                  <button key={v} onClick={() => setRenewForm(f => ({ ...f, status: v }))}
                    style={{ flex: 1, padding: '7px 4px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif",
                      background: renewForm.status === v ? `rgba(${col === '#f59e0b' ? '245,158,11' : col === '#22c55e' ? '34,197,94' : '239,68,68'},0.15)` : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${renewForm.status === v ? col : 'rgba(255,255,255,0.08)'}`,
                      color: renewForm.status === v ? col : '#64748b' }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {/* Notes */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>ملاحظات التفاوض</label>
              <textarea value={renewForm.notes} onChange={e => setRenewForm(f => ({ ...f, notes: e.target.value }))} rows={3}
                placeholder="مثال: المالك طالب زيادة ١٠٪، سأفاوض على نفس السعر..."
                style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 8, padding: '9px 12px', color: '#e2e8f0', fontSize: 13, outline: 'none', direction: 'rtl', fontFamily: "'Tajawal',sans-serif", resize: 'vertical' }} />
            </div>
            <SaveBtn onClick={saveRenewal} disabled={!renewForm.annualRent} />
          </Modal>
        );
      })()}

      {/* ── Add / Edit Modal ── */}
      {editing && (
        <Modal title={editing === 'new' ? 'إضافة عقد جديد' : `تعديل — ${form.unitName}`} onClose={() => setEditing(null)} wide>

          {/* PDF upload area */}
          <div style={{ background: 'rgba(56,189,248,0.05)', border: '2px dashed rgba(56,189,248,0.25)', borderRadius: 12, padding: '14px 16px', marginBottom: 18, textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: '#38bdf8', fontWeight: 700, marginBottom: 4 }}>📄 استيراد تلقائي من PDF إيجار</div>
            <div style={{ fontSize: 11, color: '#475569', marginBottom: 10 }}>ارفع ملف العقد وسيتم استخراج البيانات تلقائياً</div>
            <input ref={pdfRef} type="file" accept="application/pdf" onChange={handlePdfUpload} style={{ display: 'none' }} />
            <button onClick={() => pdfRef.current?.click()} disabled={parsing}
              style={{ padding: '8px 22px', borderRadius: 9, background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.3)', color: '#38bdf8', fontSize: 13, fontWeight: 700, cursor: parsing ? 'not-allowed' : 'pointer', fontFamily: "'Tajawal',sans-serif", opacity: parsing ? 0.6 : 1 }}>
              {parsing ? '⏳ جاري القراءة...' : '📂 اختر ملف PDF'}
            </button>
            {parseMsg && (
              <div style={{ fontSize: 12, color: parseMsg.startsWith('✅') ? '#22c55e' : '#f59e0b', marginTop: 8, fontWeight: 600 }}>
                {parseMsg}
              </div>
            )}
            {rawText && (
              <div style={{ marginTop: 10, textAlign: 'left' }}>
                <div style={{ fontSize: 10, color: '#475569', marginBottom: 4 }}>النص المستخرج (للتشخيص — أرسله لإبراهيم):</div>
                <textarea readOnly value={rawText} rows={6}
                  style={{ width: '100%', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px', color: '#94a3b8', fontSize: 10, fontFamily: 'monospace', direction: 'ltr', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            )}
          </div>

          {/* Form sections */}
          {formSections.map(sec => (
            <div key={sec.title} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#c8a03c', fontWeight: 700, marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid rgba(200,160,60,0.15)' }}>{sec.title}</div>
              <div style={{ display: 'grid', gridTemplateColumns: sec.cols === 1 ? '1fr' : '1fr 1fr', gap: 10 }}>
                {sec.fields.map(([label, key, type]) => (
                  <div key={key}>
                    <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4 }}>{label}</label>
                    {type === 'select' ? (
                      <select
                        value={form[key] || ''}
                        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                        style={{ ...IS({ direction: 'rtl', textAlign: 'right' }) }}>
                        <option value="">— اختر —</option>
                        {Object.entries(UNIT_TYPES).map(([k, v]) => (
                          <option key={k} value={k}>{lang === 'ar' ? v.ar : v.en}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={type === 'datetext' ? 'text' : (type || 'text')}
                        placeholder={type === 'datetext' ? 'YYYY-MM-DD' : undefined}
                        value={form[key] || ''}
                        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                        style={{ ...IS({ direction: (type === 'number' || type === 'datetext') ? 'ltr' : 'rtl', textAlign: (type === 'number' || type === 'datetext') ? 'left' : 'right' }) }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Deposit returned toggle */}
          {Number(form.depositAmount) > 0 && (
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, background: form.depositReturned ? 'rgba(34,197,94,0.06)' : 'rgba(245,158,11,0.06)', border: `1px solid ${form.depositReturned ? 'rgba(34,197,94,0.2)' : 'rgba(245,158,11,0.2)'}`, borderRadius: 10, padding: '10px 14px' }}>
              <input type="checkbox" id="dep-returned" checked={!!form.depositReturned} onChange={e => setForm(f => ({ ...f, depositReturned: e.target.checked }))}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#22c55e' }} />
              <label htmlFor="dep-returned" style={{ fontSize: 13, fontWeight: 700, color: form.depositReturned ? '#22c55e' : '#f59e0b', cursor: 'pointer' }}>
                {form.depositReturned ? '✅ تم استرداد الوديعة' : '⏳ الوديعة لم تُسترد بعد'}
              </label>
              <span style={{ fontSize: 12, color: '#64748b', marginRight: 'auto' }}>{Number(form.depositAmount).toLocaleString()} ريال</span>
            </div>
          )}

          {/* Payment cycle */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 8 }}>دورة السداد</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['1','سنوي'],['2','نصف سنوي'],['4','ربع سنوي'],['12','شهري']].map(([v, l]) => (
                <button key={v} onClick={() => setForm(f => ({ ...f, freq: v }))}
                  style={{ flex: 1, padding: '8px 4px', borderRadius: 9, border: '1px solid', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Tajawal',sans-serif",
                    background: form.freq === v ? 'rgba(200,160,60,0.15)' : 'rgba(255,255,255,0.03)',
                    color: form.freq === v ? '#c8a03c' : '#64748b',
                    borderColor: form.freq === v ? 'rgba(200,160,60,0.4)' : 'rgba(255,255,255,0.07)' }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Payment dates */}
          <div style={{ margin: '12px 0' }}>
            <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 5 }}>تواريخ الدفع (افصل بفاصلة)</label>
            <input value={form.paymentDates || ''} onChange={e => setForm(f => ({ ...f, paymentDates: e.target.value }))}
              placeholder="2026-01-01,2026-07-01"
              style={{ ...IS({ direction: 'ltr', textAlign: 'left', fontFamily: 'monospace' }) }} />
            {form.contractStart && form.freq && (
              <button onClick={() => {
                const dates = []; let d = new Date(form.contractStart);
                const freq = Number(form.freq) || 2; const step = Math.round(12 / freq);
                for (let i = 0; i < freq; i++) { dates.push(d.toISOString().slice(0, 10)); d = new Date(d); d.setMonth(d.getMonth() + step); }
                setForm(f => ({ ...f, paymentDates: dates.join(',') }));
              }} style={{ marginTop: 6, padding: '5px 12px', borderRadius: 7, background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.25)', color: '#a78bfa', fontSize: 11, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif" }}>
                ✨ توليد تلقائي من تاريخ البداية
              </button>
            )}
          </div>

          {/* Live breakeven preview */}
          {(Number(form.annualRent) > 0 || Number(form.waterAnnual) > 0 || Number(form.electricityAnnual) > 0) && (
            <div style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#f97316', marginBottom: 8 }}>نقطة التعادل الشهرية</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                {Number(form.annualRent) > 0 && (
                  <span style={{ fontSize: 11, color: '#64748b' }}>إيجار: <span style={{ color: '#ef4444', fontWeight: 700 }}>{Math.round(form.annualRent / 12).toLocaleString()}</span></span>
                )}
                {Number(form.waterAnnual) > 0 && (
                  <span style={{ fontSize: 11, color: '#64748b' }}>💧: <span style={{ color: '#38bdf8', fontWeight: 700 }}>{Math.round(form.waterAnnual / 12).toLocaleString()}</span></span>
                )}
                {Number(form.electricityAnnual) > 0 && (
                  <span style={{ fontSize: 11, color: '#64748b' }}>⚡: <span style={{ color: '#fbbf24', fontWeight: 700 }}>{Math.round(form.electricityAnnual / 12).toLocaleString()}</span></span>
                )}
                <span style={{ fontSize: 14, color: '#f97316', fontWeight: 800 }}>
                  = {Math.round(((Number(form.annualRent)||0) + (Number(form.waterAnnual)||0) + (Number(form.electricityAnnual)||0)) / 12).toLocaleString()} ريال/شهر
                </span>
              </div>
            </div>
          )}

          {/* Property Photo & Maps */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#c8a03c', fontWeight: 700, marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid rgba(200,160,60,0.15)' }}>📷 صورة الوحدة وموقعها</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {/* Photo upload */}
              <div>
                <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 6 }}>صورة الوحدة</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 80, height: 56, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.09)', background: 'rgba(255,255,255,0.03)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {form.imageUrl
                      ? <img src={form.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontSize: 22, opacity: 0.2 }}>🏠</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <input ref={photoRef} type="file" accept="image/*,.heic,.heif" onChange={handlePhotoUpload} style={{ display: 'none' }} />
                    <button onClick={() => photoRef.current?.click()}
                      style={{ padding: '6px 12px', borderRadius: 8, background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif", width: '100%', marginBottom: 5 }}>
                      📂 رفع صورة
                    </button>
                    {form.imageUrl && (
                      <button onClick={() => setForm(f => ({ ...f, imageUrl: '' }))}
                        style={{ padding: '4px 10px', borderRadius: 7, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', fontSize: 10, cursor: 'pointer', fontFamily: "'Tajawal',sans-serif", width: '100%' }}>
                        🗑 حذف الصورة
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {/* Maps URL */}
              <div>
                <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 6 }}>📍 رابط جوجل ماب</label>
                <input value={form.mapsUrl || ''} onChange={e => setForm(f => ({ ...f, mapsUrl: e.target.value }))}
                  placeholder="https://maps.google.com/..." style={{ ...IS({ direction: 'ltr', textAlign: 'left' }) }} />
                {form.mapsUrl && (
                  <a href={form.mapsUrl} target="_blank" rel="noreferrer"
                    style={{ display: 'inline-block', marginTop: 6, fontSize: 11, color: '#22c55e', textDecoration: 'none', background: 'rgba(34,197,94,0.08)', padding: '4px 10px', borderRadius: 7, border: '1px solid rgba(34,197,94,0.2)' }}>
                    📍 فتح الموقع على الخريطة →
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 5 }}>ملاحظات</label>
            <input value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              style={{ ...IS({ direction: 'rtl' }) }} />
          </div>

          <SaveBtn onClick={save} disabled={!form.unitNumber} />
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// OPS MANAGER TAB — حامد
// ══════════════════════════════════════════════════════════════════
